import React, { useState } from "react";
import Card from "components/card";
import { MdClose, MdCardTravel } from "react-icons/md";
import { FaPassport } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

const VisaModal = ({ isOpen, onClose, onAdd, editingItem }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    processing: "",
    price: "",
    applicationDate: ""
  });

  React.useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name || "",
        type: editingItem.type || "",
        processing: editingItem.processing || "",
        price: editingItem.price || "",
        applicationDate: editingItem.applicationDate || ""
      });
    } else {
      setFormData({ name: "", type: "", processing: "", price: "", applicationDate: "" });
    }
  }, [editingItem, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({ name: "", type: "", processing: "", price: "", applicationDate: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[500px] max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border border-white/20 shadow-2xl">
        <div className="bg-[#5a88c6] shadow-lg p-6 rounded-t-[20px] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3 border border-white/30">
                <FaPassport className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">{editingItem ? 'Edit Visa Service' : 'Add Visa Service'}</h3>
                <p className="text-white/80 text-sm flex items-center space-x-1">
                  <HiSparkles className="h-4 w-4" />
                  <span>Fast Track Processing</span>
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <MdCardTravel className="h-4 w-4 text-rose-500" />
                <span>Visa Service Name</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Tourist Visa - USA"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-rose-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-rose-300"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-pink-500">📜</span>
                <span>Visa Type</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-rose-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-rose-300"
                required
              >
                <option value="">Select Visa Type</option>
                <option value="B1/B2 Visa">B1/B2 Tourist/Business</option>
                <option value="Tier 2 Visa">Tier 2 Work Visa</option>
                <option value="Transit Visa">Transit Visa</option>
                <option value="Student Visa">Student Visa</option>
                <option value="Work Permit">Work Permit</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-purple-500">📅</span>
                <span>Application Date</span>
              </label>
              <input
                type="date"
                value={formData.applicationDate}
                onChange={(e) => setFormData({...formData, applicationDate: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-rose-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-rose-300"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-orange-500">⏱️</span>
                <span>Processing Time</span>
              </label>
              <select
                value={formData.processing}
                onChange={(e) => setFormData({...formData, processing: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-rose-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-rose-300"
                required
              >
                <option value="">Select Processing Time</option>
                <option value="1-3 Days">1-3 Days (Express)</option>
                <option value="3-5 Days">3-5 Days (Standard)</option>
                <option value="5-7 Days">5-7 Days (Regular)</option>
                <option value="10-15 Days">10-15 Days (Normal)</option>
                <option value="15-30 Days">15-30 Days (Economy)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                <span className="text-green-500">💰</span>
                <span>Service Fee</span>
              </label>
              <input
                type="text"
                placeholder="e.g., $299"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full rounded-xl border-2 border-slate-200 p-3 focus:border-rose-500 focus:outline-none bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-rose-300"
                required
              />
            </div>
          </div>
          
          <div className="flex space-x-4 pt-6">
            <button
              type="submit"
              className="flex-1 rounded-xl bg-[#5a88c6] py-3 px-6 text-white font-semibold hover:bg-[#4a7bb6] transition-all duration-200"
            >
              {editingItem ? 'Update Visa Service' : 'Add Visa Service'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl bg-slate-100 py-3 px-6 text-slate-700 font-semibold hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default VisaModal;